/**
 * AuthContext — application-wide auth and session state.
 *
 * Wraps {@link useAuth} so that any component in the tree can read
 * `profile`, `impersonation`, `factors`, etc. without prop-drilling.
 *
 * Usage:
 *   // Read auth state anywhere below <AuthProvider>
 *   const { profile, impersonation, factors } = useAuthContext();
 *
 * In tests, use {@link TestAuthProvider} instead of AuthProvider.
 */
import { createContext, ReactNode, useContext, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { useAuth } from '../hooks/useAuth';
import type {
  DecryptedUser,
  Factors,
  ImpersonationRecord,
  Profile,
} from '../shared/types/appTypes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoaded: boolean;
  user: DecryptedUser | null;
  profile: Profile | null;
  impersonation: ImpersonationRecord | null;
  setImpersonation: (record: ImpersonationRecord | null) => void;
  factors: Factors;
  clearState: () => void;
  adminCheck: (
    isAdminPath: boolean,
    profile: Profile | null,
    impersonation: ImpersonationRecord | null,
  ) => boolean;
  handleAuthentication: (
    authenticated: boolean,
    userRecord: DecryptedUser | string,
  ) => Promise<void>;
  endImpersonation: () => void;
  homePage: string;
  scope: string;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode;
  /** Optional pre-wired toast ref — if omitted, AuthProvider creates its own. */
  toast?: React.RefObject<Toast | null>;
}

/**
 * Mount once at the app root (inside BrowserRouter).
 * Exposes the full return value of useAuth via context.
 */
export function AuthProvider({
  children,
  toast: externalToast,
}: AuthProviderProps) {
  const internalToast = useRef<Toast>(null);
  const toastRef = externalToast ?? internalToast;
  const auth = useAuth(toastRef);

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the current auth context value.
 * Must be called inside a component rendered below <AuthProvider>.
 */
export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within an <AuthProvider>');
  }
  return ctx;
}

export { AuthContext };
